import { IconBtn, NumberField, Section } from './parts'
import { fmtNum, scaleLabel } from './format'
import { storeyCotaLabel, storeyCotaTitle, type Storey2D } from './storeys'
import type { Vec3 } from './lib'
import type { SavedView2D } from './views2dTypes'

// ---------------------------------------------------------------------------
// Navegador de vistas: a coluna esquerda do MODO 2D. É o conteúdo que
// vivia na coluna esquerda da antiga janela flutuante, re-alojado.
//
// Duas metades: CRIAR (planta por piso, planta livre, corte da caixa /
// por azimute) e o que JÁ EXISTE (vistas gravadas, agrupadas em plantas
// e cortes, com abrir / duplicar / renomear / apagar). A vista aberta
// fica assinalada.
// ---------------------------------------------------------------------------

export interface ViewNavigatorProps {
  storeys: Storey2D[]
  storeysBusy: boolean
  busy: boolean
  cutOffset: number
  onCreatePlan: (s: Storey2D) => void

  freeElevation: number
  setFreeElevation: (v: number) => void
  freeDepth: number
  setFreeDepth: (v: number) => void
  onCreateFreePlan: () => void
  /** Cotas mínima e máxima da geometria do modelo, em metros. Serve para
   *  quem escreve uma cota de corte à mão saber em que gama é que há
   *  alguma coisa. Ausente enquanto a caixa do modelo não chegou. */
  geometryRange?: [number, number] | null

  onCreateFromSectionBox: () => void
  manualName: string
  setManualName: (v: string) => void
  manualAzimuth: number
  setManualAzimuth: (v: number) => void
  manualDepth: number
  setManualDepth: (v: number) => void
  manualOrigin: Vec3
  setManualOrigin: (fn: (o: Vec3) => Vec3) => void
  onCreateManualSection: () => void

  saved: SavedView2D[]
  currentViewId: string | null
  onOpenSaved: (v: SavedView2D) => void
  onRenameSaved: (v: SavedView2D) => void
  onDeleteSaved: (v: SavedView2D) => void
  onDuplicateSaved: (v: SavedView2D) => void
}

function ViewNavigator(props: ViewNavigatorProps) {
  const {
    storeys,
    storeysBusy,
    busy,
    cutOffset,
    onCreatePlan,
    freeElevation,
    setFreeElevation,
    freeDepth,
    setFreeDepth,
    onCreateFreePlan,
    geometryRange,
    onCreateFromSectionBox,
    manualName,
    setManualName,
    manualAzimuth,
    setManualAzimuth,
    manualDepth,
    setManualDepth,
    manualOrigin,
    setManualOrigin,
    onCreateManualSection,
    saved,
    currentViewId,
    onOpenSaved,
    onRenameSaved,
    onDeleteSaved,
    onDuplicateSaved,
  } = props

  const savedPlans = saved.filter((v) => v.def.kind === 'plan')
  const savedSections = saved.filter((v) => v.def.kind !== 'plan')

  const savedRow = (v: SavedView2D) => (
    <div
      key={v.id}
      className={`flex items-center gap-1 rounded px-1 py-0.5 hover:bg-[var(--color-bg)] ${
        currentViewId === v.id ? 'bg-[var(--color-accent-bg)]' : ''
      }`}
    >
      <button
        type="button"
        onClick={() => onOpenSaved(v)}
        disabled={busy}
        title={`Abrir "${v.name}" (${
          v.def.kind === 'plan' ? 'planta' : 'corte'
        }, ${scaleLabel(v.scale)})`}
        className={`min-w-0 flex-1 truncate text-left disabled:opacity-40 ${
          currentViewId === v.id
            ? 'text-[var(--color-accent)]'
            : 'text-[var(--color-text)]'
        }`}
      >
        {v.name}
      </button>
      <IconBtn label="⧉" title="Duplicar" onClick={() => onDuplicateSaved(v)} />
      <IconBtn label="✎" title="Renomear" onClick={() => onRenameSaved(v)} />
      <IconBtn
        label="×"
        title="Apagar"
        danger
        onClick={() => onDeleteSaved(v)}
      />
    </div>
  )

  return (
    <div
      data-testid="views2d-navigator"
      className="flex w-full shrink-0 flex-col gap-2 text-[11px]"
    >
      <Section title={`Vistas gravadas (${saved.length})`}>
        {saved.length === 0 && (
          <div className="px-1 py-1 text-[10px] text-[var(--color-text-muted)]">
            Ainda não gravaste nenhuma vista deste modelo. Cria uma planta ou um
            corte aqui em baixo.
          </div>
        )}
        {savedPlans.length > 0 && (
          <>
            <div className="px-1 pt-0.5 text-[9px] uppercase tracking-wider text-[var(--color-text-dim)]">
              Plantas
            </div>
            {savedPlans.map(savedRow)}
          </>
        )}
        {savedSections.length > 0 && (
          <>
            <div className="px-1 pt-0.5 text-[9px] uppercase tracking-wider text-[var(--color-text-dim)]">
              Cortes
            </div>
            {savedSections.map(savedRow)}
          </>
        )}
      </Section>

      <Section title="Planta por piso">
        {storeysBusy && (
          <div className="px-1 py-1 text-[10px] text-[var(--color-text-muted)]">
            A ler os pisos…
          </div>
        )}
        {!storeysBusy && storeys.length === 0 && (
          <div className="px-1 py-1 text-[10px] text-[var(--color-text-muted)]">
            Sem IfcBuildingStorey com cota legível neste modelo. Usa a planta
            livre.
          </div>
        )}
        <div>
          {storeys.map((s) => (
            <button
              key={s.localId}
              type="button"
              onClick={() => onCreatePlan(s)}
              disabled={busy}
              title={storeyCotaTitle(s, cutOffset)}
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left hover:bg-[var(--color-bg)] disabled:opacity-40"
            >
              <span className="min-w-0 flex-1 truncate text-[var(--color-text)]">
                {s.name}
              </span>
              <span className="shrink-0 font-mono text-[9px] text-[var(--color-text-dim)]">
                {storeyCotaLabel(s)}
              </span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Planta livre">
        <div className="flex flex-col gap-1">
          <NumberField
            label="Cota de corte (m)"
            value={freeElevation}
            step={0.1}
            onChange={setFreeElevation}
            title="Cota ABSOLUTA do plano de corte no referencial do mundo (a mesma escala das cotas dos pisos acima). Não leva o offset de 1,20 m."
          />
          <NumberField
            label="Profundidade (m)"
            value={freeDepth}
            step={0.5}
            min={0.01}
            onChange={setFreeDepth}
            title="Até que distância abaixo do plano de corte se desenham arestas."
          />
          {storeys.length > 0 && (
            <div className="px-1 text-[9px] leading-snug text-[var(--color-text-dim)]">
              Pisos:{' '}
              {storeys.map((s) => `${s.name} ${storeyCotaLabel(s)}`).join(' · ')}
            </div>
          )}
          {geometryRange && (
            <div
              data-testid="views2d-geometry-range"
              className="px-1 text-[9px] leading-snug text-[var(--color-text-dim)]"
              title="Cotas em que há mesmo geometria no modelo. Uma cota fora desta gama dá uma planta vazia; dentro dela pode dar uma vista só de projecção, que é legítima."
            >
              Geometria entre {fmtNum(geometryRange[0])} e{' '}
              {fmtNum(geometryRange[1])} m
            </div>
          )}
          <button
            type="button"
            onClick={onCreateFreePlan}
            disabled={busy}
            className="w-full rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1 text-[11px] text-[var(--color-accent)] disabled:opacity-40"
          >
            Criar planta livre
          </button>
        </div>
      </Section>

      <Section title="Criar corte">
        <button
          type="button"
          onClick={onCreateFromSectionBox}
          disabled={busy}
          title="Usa a face da caixa de corte que está virada para a câmara como plano de corte. A profundidade e a janela do desenho são as da própria caixa."
          className="mb-1 w-full rounded border border-[var(--color-border)] px-2 py-1 text-left hover:bg-[var(--color-bg)] disabled:opacity-40"
        >
          Da caixa de corte
        </button>
        <div className="flex flex-col gap-1 rounded border border-[var(--color-border-subtle)] p-1.5">
          <input
            type="text"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder="Nome do corte"
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5 text-[11px] outline-none focus:border-[var(--color-accent)]"
          />
          <div className="flex items-center gap-1">
            {(
              [
                ['N', 0],
                ['E', 90],
                ['S', 180],
                ['O', 270],
              ] as const
            ).map(([label, az]) => (
              <button
                key={label}
                type="button"
                onClick={() => setManualAzimuth(az)}
                title={`Olhar para ${label} (azimute ${az}°)`}
                className={`flex-1 rounded border px-1 py-0.5 text-[10px] ${
                  manualAzimuth === az
                    ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                    : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <NumberField
            label="Azimute (°)"
            value={manualAzimuth}
            step={5}
            onChange={setManualAzimuth}
            title="Direcção de observação em graus a partir do Norte, sentido horário. 0 = olhar para Norte."
          />
          <NumberField
            label="Profundidade (m)"
            value={manualDepth}
            step={1}
            min={0.1}
            onChange={setManualDepth}
            title="Até que distância para lá do plano de corte se desenha."
          />
          <div className="grid grid-cols-3 gap-1">
            {(['X', 'Y', 'Z'] as const).map((axis, i) => (
              <NumberField
                key={axis}
                label={axis}
                value={manualOrigin[i]}
                step={0.5}
                compact
                onChange={(v) =>
                  setManualOrigin((o) => {
                    const next: Vec3 = [...o]
                    next[i] = v
                    return next
                  })
                }
                title={`Ponto do plano de corte, coordenada ${axis} do mundo (metros). Por omissão o centro do modelo.`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onCreateManualSection}
            disabled={busy}
            className="w-full rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1 text-[11px] text-[var(--color-accent)] disabled:opacity-40"
          >
            Criar corte por azimute
          </button>
        </div>
      </Section>
    </div>
  )
}

export default ViewNavigator
